/* 관리 API 클라이언트 — 순수(React 비의존). plan.md §5 / docs/ops/admin-api.md.
   ★ 동결 인터페이스(S2~S4 변경 금지). 첫 인자 getIdToken = session.getIdToken.
       listUsers(getIdToken, {limit=60, paginationToken}={})
         => Promise<{users: ManagedUser[], paginationToken?}>
       setUserGroup(getIdToken, username, group, action) => Promise<void>   // group:'admin'|'developer', action:'add'|'remove'
       deleteUser(getIdToken, username) => Promise<{username, identity_id, s3_objects_deleted}>
       listRequests(getIdToken, status='pending') => Promise<{requests: TeacherRequest[]}>
       resolveRequest(getIdToken, requestId, action) => Promise<{request_id, status}>  // action:'approve'|'reject'

   S1 = fake 구현(localStorage 를 가짜 백엔드로, 시드 사용자/요청 반환·변경).
   S3 = real fetch 구현(이 파일). fetch(ADMIN_API_URL, Authorization: Bearer <getIdToken()>).
        fake 분기(isFakeMode)는 보존 — env 없으면 오프라인 데모로 동작.

   확장(비동결): getInstallStats(getIdToken) — 앱/펌웨어 설치파일 다운로드 집계 조회.
        데이터는 S3 액세스 로그를 집계 Lambda 가 DynamoDB(huenit-install-stats)에 적재한 것.
        엔드포인트 미배포 시 404 → 호출부가 빈 상태로 안내(infra/aws/install-stats/README).

   ManagedUser    = {username,email,nickname,groups:[],enabled,status,created_at}
   TeacherRequest = {request_id,user_sub,email,nickname,message,status,created_at,resolved_at,resolved_by}
   InstallStat    = {version,downloads,last_at,first_at}  (firmware 는 type 추가) */
import { isFakeMode, ADMIN_API_URL } from '../config/env.js';

const U_KEY = 'adm.fake.users';
const R_KEY = 'adm.fake.requests';
const delay = (ms = 220) => new Promise(r => setTimeout(r, ms));

/* fake: 최근 N일 내 무작위 시각(데모 표시용). 결정성 불필요(오프라인 데모 전용). */
function agoIso(daysAgo, hour = 10) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, (daysAgo * 7) % 60, 0, 0);
  return d.toISOString();
}
function seedInstallStats() {
  return {
    app: [
      { version: '2.0.66', downloads: 128, first_at: agoIso(9), last_at: agoIso(0, 14) },
      { version: '2.0.65', downloads: 94, first_at: agoIso(18), last_at: agoIso(1, 9) },
      { version: '2.0.64', downloads: 47, first_at: agoIso(27), last_at: agoIso(5, 18) },
      { version: '2.0.63', downloads: 21, first_at: agoIso(40), last_at: agoIso(12, 11) },
    ],
    firmware: [
      { type: 'camera', version: '2.0.66', downloads: 61, first_at: agoIso(9), last_at: agoIso(0, 15) },
      { type: 'camera', version: '2.0.65', downloads: 38, first_at: agoIso(18), last_at: agoIso(2, 8) },
      { type: 'main', version: '1.4.2', downloads: 44, first_at: agoIso(15), last_at: agoIso(1, 16) },
    ],
  };
}

/* ---------- fake 시드 ---------- */
function seedUsers() {
  return [
    { username: 'satgym@lamaro.me', email: 'satgym@lamaro.me', nickname: '개발자', groups: ['developer'], enabled: true, status: 'CONFIRMED', created_at: '2026-01-04T02:10:00Z' },
    { username: 'jiwoo.kim@example.com', email: 'jiwoo.kim@example.com', nickname: '김지우', groups: ['admin'], enabled: true, status: 'CONFIRMED', created_at: '2026-02-11T05:30:00Z' },
    { username: 'minseo.lee@example.com', email: 'minseo.lee@example.com', nickname: '이민서', groups: ['admin'], enabled: true, status: 'CONFIRMED', created_at: '2026-03-02T08:00:00Z' },
    { username: 'hyun.park@example.com', email: 'hyun.park@example.com', nickname: '박현', groups: [], enabled: true, status: 'CONFIRMED', created_at: '2026-03-19T11:45:00Z' },
    { username: 'somi.jung@example.com', email: 'somi.jung@example.com', nickname: '정소미', groups: [], enabled: true, status: 'CONFIRMED', created_at: '2026-04-07T01:20:00Z' },
    { username: 'tester@example.com', email: 'tester@example.com', nickname: null, groups: [], enabled: false, status: 'UNCONFIRMED', created_at: '2026-05-21T13:05:00Z' },
  ];
}
function seedRequests() {
  return [
    { request_id: 'req_1001', user_sub: 'sub-hyun', email: 'hyun.park@example.com', nickname: '박현', message: '학교 방과후 수업에서 학생들에게 공유할 예제를 만들고 싶어 선생님 권한을 신청합니다.', status: 'pending', created_at: '2026-06-05T09:12:00Z', resolved_at: null, resolved_by: null },
    { request_id: 'req_1002', user_sub: 'sub-somi', email: 'somi.jung@example.com', nickname: '정소미', message: '코딩 동아리 운영용으로 예제 큐레이션 권한이 필요합니다.', status: 'pending', created_at: '2026-06-06T14:40:00Z', resolved_at: null, resolved_by: null },
    { request_id: 'req_0990', user_sub: 'sub-jiwoo', email: 'jiwoo.kim@example.com', nickname: '김지우', message: '메이커 교육 강사입니다.', status: 'approved', created_at: '2026-05-28T03:00:00Z', resolved_at: '2026-05-29T06:30:00Z', resolved_by: 'satgym@lamaro.me' },
  ];
}

function load(key, seed) {
  try {
    const s = localStorage.getItem(key);
    if (s) return JSON.parse(s);
  } catch (e) { /* ignore */ }
  const init = seed();
  try { localStorage.setItem(key, JSON.stringify(init)); } catch (e) { /* ignore */ }
  return init;
}
function save(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* ignore */ } }

/* ---------- real fetch (live) ----------
   비-2xx면 throw. err.status 로 호출부가 401(인증만료)/403(권한없음) 구분.
   Lambda 오류 응답 shape = {"message": "..."} (docs/ops/admin-api.md §4). */
class ApiError extends Error {
  constructor(message, status) { super(message); this.name = 'ApiError'; this.status = status; }
}

function errorLabel(status, serverMsg) {
  if (status === 401) return '인증이 만료되었습니다. 다시 로그인해 주세요.';
  if (status === 403) return '권한이 없습니다. 개발자 계정으로 로그인해야 합니다.';
  return serverMsg || `요청이 실패했습니다 (HTTP ${status}).`;
}

async function req(getIdToken, method, path, body) {
  const token = await getIdToken();
  if (!token) throw new ApiError(errorLabel(401), 401);   // 세션 없음 → 인증만료와 동일 처리

  let res;
  try {
    res = await fetch(ADMIN_API_URL + path, {
      method,
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: body != null ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    // 네트워크 단절 또는 CORS preflight 실패(§9.2 프로비저닝 갭 미완료 시 여기로 떨어짐).
    throw new ApiError('관리 API에 연결할 수 없습니다. (네트워크 또는 API Gateway CORS 설정을 확인하세요.)', 0);
  }

  if (!res.ok) {
    let serverMsg = '';
    try { const data = await res.json(); serverMsg = data && (data.message || data.error) || ''; }
    catch (e) { /* 비 JSON 본문 */ }
    throw new ApiError(errorLabel(res.status, serverMsg), res.status);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/* ---------- API ---------- */
export async function listUsers(getIdToken, { limit = 60, paginationToken } = {}) {
  if (isFakeMode) {
    await delay();
    const users = load(U_KEY, seedUsers);
    return { users: users.slice(0, limit) };
  }
  const qs = new URLSearchParams();
  if (limit) qs.set('limit', String(limit));
  if (paginationToken) qs.set('pagination_token', paginationToken);
  const q = qs.toString();
  const data = await req(getIdToken, 'GET', '/users' + (q ? '?' + q : ''));
  // 응답은 snake(pagination_token) → 동결 시그니처는 camel(paginationToken) 로 반환.
  return { users: data.users || [], paginationToken: data.pagination_token || undefined };
}

export async function setUserGroup(getIdToken, username, group, action) {
  if (isFakeMode) {
    await delay();
    const users = load(U_KEY, seedUsers);
    const u = users.find(x => x.username === username);
    if (!u) throw new Error('사용자를 찾을 수 없습니다.');
    const set = new Set(u.groups || []);
    if (action === 'add') set.add(group); else set.delete(group);
    u.groups = [...set];
    save(U_KEY, users);
    return;
  }
  await req(getIdToken, 'POST', `/users/${encodeURIComponent(username)}/groups`, { group, action });
}

export async function deleteUser(getIdToken, username) {
  if (isFakeMode) {
    await delay();
    const users = load(U_KEY, seedUsers);
    const next = users.filter(x => x.username !== username);
    save(U_KEY, next);
    return { username, identity_id: 'fake-identity', s3_objects_deleted: 0 };
  }
  return await req(getIdToken, 'DELETE', `/users/${encodeURIComponent(username)}`);
}

export async function listRequests(getIdToken, status = 'pending') {
  if (isFakeMode) {
    await delay();
    const requests = load(R_KEY, seedRequests)
      .filter(r => r.status === status)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));   // 시간 역순
    return { requests };
  }
  const data = await req(getIdToken, 'GET', '/requests?status=' + encodeURIComponent(status));
  return { requests: data.requests || [] };
}

export async function resolveRequest(getIdToken, requestId, action) {
  if (isFakeMode) {
    await delay();
    const requests = load(R_KEY, seedRequests);
    const r = requests.find(x => x.request_id === requestId);
    if (!r) throw new Error('요청을 찾을 수 없습니다.');
    r.status = action === 'approve' ? 'approved' : 'rejected';
    r.resolved_at = new Date().toISOString();
    r.resolved_by = 'fake-developer';
    save(R_KEY, requests);
    // approve → 요청자를 admin(선생님) group 으로 편입 (fake)
    if (action === 'approve') {
      const users = load(U_KEY, seedUsers);
      const u = users.find(x => x.email === r.email);
      if (u && !(u.groups || []).includes('admin')) { u.groups = [...(u.groups || []), 'admin']; save(U_KEY, users); }
    }
    return { request_id: requestId, status: r.status };
  }
  return await req(getIdToken, 'POST', `/requests/${encodeURIComponent(requestId)}`, { action });
}

/* 설치(다운로드) 통계 — 버전별 설치파일 다운로드 수 + 최근 다운로드 시각.
   반환: { app: InstallStat[], firmware: (InstallStat & {type})[], generated_at }.
   집계 백엔드 미배포(404) 는 "빈 데이터"로 폴백 — 화면이 안내 문구로 처리(throw 안 함). */
export async function getInstallStats(getIdToken) {
  if (isFakeMode) {
    await delay();
    return { ...seedInstallStats(), generated_at: new Date().toISOString() };
  }
  let data;
  try {
    data = await req(getIdToken, 'GET', '/install-stats');
  } catch (e) {
    if (e && e.status === 404) return { app: [], firmware: [], generated_at: null, notReady: true };
    throw e;
  }
  return {
    app: Array.isArray(data && data.app) ? data.app : [],
    firmware: Array.isArray(data && data.firmware) ? data.firmware : [],
    generated_at: (data && data.generated_at) || null,
  };
}
